// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import http from "@http";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import { transformError } from "@transformers/response";
import {
  GetReferralList,
  GetReferral,
  GetReferralSchema,
  AddReferral,
  StartDraftReferral,
  UpdateReferral,
  UpdateReferralAssignments,
} from "@modules/service/constants/actions";

import { ApprovalStateEnum } from "@modules/service/constants";
import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import type { Referral } from "@modules/service/reducer/referrals/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type ReferralListRequest = $Exact<{
  clientId: string,
  isActive: boolean | null,
}>;

export function* onGetReferralList({
  payload,
}: Action<ReferralListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<Referral>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process`,
      {
        params: { isActive: payload.isActive },
      },
    );

    yield put(actionCreators.GetReferralListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReferralListError(transformError(e)));
  }
}

type ReferralRequest = ReferralListRequest &
  $Exact<{
    processId: string,
  }>;

export function* onGetReferral({
  payload,
}: Action<ReferralRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Referral>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const stableResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/assignment`,
    );

    yield all([
      put(actionCreators.GetReferralSuccess(stableResponse.data)),
      put(actionCreators.SetReferralAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetReferralError(transformError(e)));
  }
}

type RefreshRequest = $Exact<{
  clientId: string,
  processId?: string,
  isActive?: null | boolean,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  yield put(
    actionCreators.GetReferralList({ clientId: params.clientId, isActive: params.isActive }),
  );

  if (params.processId) {
    yield put(
      actionCreators.GetReferral({
        clientId: params.clientId,
        processId: params.processId,
      }),
    );
  }
}

// CREATE

export function* onGetReferralSchema({
  payload,
}: Action<ReferralListRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("ReferralProcessRevisionDTO", { clientId: payload.clientId });

    yield put(actionCreators.GetReferralSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetReferralSchemaError(transformError(e)));
  }
}

export function* onStartDraftReferral({
  payload,
}: Action<ReferralRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Referral>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}`,
    );

    yield all([put(actionCreators.StartDraftReferralSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.StartDraftReferralError(transformError(e)));
  }
}

type ReferralAddRequest = ReferralListRequest &
  $Exact<{
    draft: Referral,
    assignments: Array<HierarchyAssignment>,
    currentFilter?: boolean,
  }>;

export function* onAddReferral({ payload }: Action<ReferralAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(sendSuccessMessage("REFERRAL_PROCESS_CREATED")),
      put(actionCreators.AddReferralSuccess(response.data)),
      call(onRefresh, { clientId: payload.clientId, isActive: payload.currentFilter }),
    ]);
  } catch (e) {
    yield put(actionCreators.AddReferralError(transformError(e.response)));
  }
}

// UPDATE

type ReferralUpdateRequest = ReferralRequest &
  $Exact<{
    draft: Referral,
  }>;

export function* onUpdateReferral({
  payload,
}: Action<ReferralUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}`,
      payload.draft,
    );

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(sendSuccessMessage("REFERRAL_PROCESS_UPDATED")),
      put(actionCreators.UpdateReferralSuccess(response.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateReferralError(transformError(e.response)));
  }
}

type ReferralUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<Referral>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateReferralAssignments({
  payload,
}: Action<ReferralUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/referral/process/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/referral/process/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/referral/process/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      processId: payload.container.id,
    });

    yield put(actionCreators.UpdateReferralAssignmentsSuccess());
  } catch (e) {
    yield put(actionCreators.UpdateReferralAssignmentsError(transformError(e)));
  }
}

// ROOT

export function* ReferralSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetReferralList, onGetReferralList),
    takeEvery(GetReferral, onGetReferral),
    takeEvery(GetReferralSchema, onGetReferralSchema),
    takeEvery(AddReferral, onAddReferral),
    takeEvery(StartDraftReferral, onStartDraftReferral),
    takeEvery(UpdateReferral, onUpdateReferral),
    takeEvery(UpdateReferralAssignments, onUpdateReferralAssignments),
  ]);
}

export default ReferralSagas;
